import Phaser from 'phaser';
import { SPRITE_CATALOG } from './BootScene.js';
import { getSavedStyle } from './StyleAuditionScene.js';

// Asset preview lab. Access via #lab in the URL.
// For every textureKey in SPRITE_CATALOG, attempts to also load assets/sprites/<key>.png
// and assets/_dev/<key>.png. If any real PNG exists, renders it side-by-side with the
// programmatic placeholder. Drop a candidate into _dev/ to preview before committing.

const REAL_PREFIX_SHIPPED = 'real:';
const REAL_PREFIX_DEV = 'dev:';

const PADDING = 28;
const CARD_W = 210;
const CARD_H = 156;
const CARD_GAP = 14;
const SECTION_HEADER_H = 36;
const SECTION_GAP = 22;
const SLOT_W = (CARD_W - 18) / 3; // three slots: placeholder | shipped | dev
const SLOT_H = 72;

export class LabScene extends Phaser.Scene {
  constructor() {
    super('LabScene');
  }

  preload() {
    // Silently swallow 404s so missing optional assets don't spam the console
    this.load.on('loaderror', (file) => {
      if (file.key?.startsWith(REAL_PREFIX_SHIPPED) || file.key?.startsWith(REAL_PREFIX_DEV)) {
        // expected when a real asset doesn't exist yet
      } else {
        console.warn('Lab failed to load asset:', file.key, file.url);
      }
    });

    for (const section of SPRITE_CATALOG) {
      for (const item of section.items) {
        // Try shipped real asset (committed to repo)
        this.load.image(`${REAL_PREFIX_SHIPPED}${item.key}`, `assets/sprites/${item.key}.png`);
        // Try dev sandbox asset (gitignored)
        this.load.image(`${REAL_PREFIX_DEV}${item.key}`, `assets/_dev/${item.key}.png`);
      }
    }
  }

  create() {
    const cam = this.cameras.main;
    cam.setBackgroundColor('#0a0806');
    cam.fadeIn(280, 0, 0, 0);

    this._buildHeader();
    this._buildSections();
    this._setupScroll();
    this._buildFooter();
  }

  _buildHeader() {
    const cam = this.cameras.main;
    const headerBg = this.add.rectangle(0, 0, cam.width, 60, 0x14110e, 1)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(900);

    this.add.text(PADDING, 14, 'ASSET LAB', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#f6b93b',
      fontStyle: 'bold',
    })
      .setLetterSpacing?.(3)
      .setScrollFactor(0)
      .setDepth(901);

    this.add.text(PADDING, 36, 'Programmatic placeholders · drop real sprites in /public/assets/_dev/<key>.png to preview', {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#f4ead5',
    })
      .setAlpha(0.65)
      .setScrollFactor(0)
      .setDepth(901);

    // Right-side hints: style pick + audition link
    const savedStyle = getSavedStyle();
    const styleHint = savedStyle
      ? `style: ${savedStyle}  ·  visit #audition to revisit`
      : 'no style picked yet  ·  visit #audition to choose';
    this.add.text(cam.width - PADDING, 22, styleHint, {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: savedStyle ? '#9bd28a' : '#f6b93b',
    })
      .setOrigin(1, 0.5)
      .setAlpha(0.85)
      .setScrollFactor(0)
      .setDepth(901);
    this.add.text(cam.width - PADDING, 40, 'dev mode', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#f6b93b',
    })
      .setOrigin(1, 0.5)
      .setAlpha(0.6)
      .setScrollFactor(0)
      .setDepth(901);
  }

  _buildSections() {
    const cam = this.cameras.main;
    const startY = 80;
    const usableW = cam.width - PADDING * 2;
    const cardsPerRow = Math.max(1, Math.floor((usableW + CARD_GAP) / (CARD_W + CARD_GAP)));

    let cursorY = startY;

    for (const section of SPRITE_CATALOG) {
      // Section header
      this.add.text(PADDING, cursorY, section.section.toUpperCase(), {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#f4ead5',
      })
        .setLetterSpacing?.(2)
        .setAlpha(0.9);
      this.add.rectangle(PADDING + 240, cursorY + 6, usableW - 240, 1, 0xf4ead5, 0.18)
        .setOrigin(0, 0);

      cursorY += SECTION_HEADER_H;

      // Cards
      let col = 0;
      for (const item of section.items) {
        const x = PADDING + col * (CARD_W + CARD_GAP);
        this._buildCard(x, cursorY, item);
        col += 1;
        if (col >= cardsPerRow) {
          col = 0;
          cursorY += CARD_H + CARD_GAP;
        }
      }
      if (col !== 0) cursorY += CARD_H + CARD_GAP;
      cursorY += SECTION_GAP;
    }

    this.contentBottom = cursorY;
  }

  _buildCard(x, y, item) {
    // Card background
    this.add.rectangle(x, y, CARD_W, CARD_H, 0x14110e, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0xf4ead5, 0.15);

    // Three slot labels
    const slots = [
      { key: item.key, label: 'placeholder', exists: true },
      { key: `${REAL_PREFIX_SHIPPED}${item.key}`, label: 'shipped', exists: this.textures.exists(`${REAL_PREFIX_SHIPPED}${item.key}`) },
      { key: `${REAL_PREFIX_DEV}${item.key}`, label: 'dev', exists: this.textures.exists(`${REAL_PREFIX_DEV}${item.key}`) },
    ];

    slots.forEach((slot, i) => {
      const sx = x + 8 + i * (SLOT_W + 1);
      const sy = y + 8;

      // Slot frame
      this.add.rectangle(sx, sy, SLOT_W, SLOT_H, 0x0a0806, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, 0xf4ead5, slot.exists ? 0.25 : 0.08);

      if (slot.exists) {
        // Render sprite scaled to fit the slot, capped at 4x
        const tex = this.textures.get(slot.key);
        const src = tex.getSourceImage();
        const sw = src.width;
        const sh = src.height;
        const fitScale = Math.min(SLOT_W / sw, SLOT_H / sh, 4);
        this.add.image(sx + SLOT_W / 2, sy + SLOT_H / 2, slot.key)
          .setScale(fitScale);
      } else {
        this.add.text(sx + SLOT_W / 2, sy + SLOT_H / 2, '—', {
          fontFamily: 'monospace',
          fontSize: '14px',
          color: '#f4ead5',
        }).setOrigin(0.5).setAlpha(0.25);
      }

      // Slot caption
      this.add.text(sx + SLOT_W / 2, sy + SLOT_H + 4, slot.label, {
        fontFamily: 'monospace',
        fontSize: '8px',
        color: slot.exists ? '#9bd28a' : '#7a6f5a',
      }).setOrigin(0.5, 0).setAlpha(0.85);
    });

    // Sprite name + key + size
    const labelY = y + 8 + SLOT_H + 18;
    this.add.text(x + 10, labelY, item.label, {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#f4ead5',
    });
    this.add.text(x + 10, labelY + 14, `${item.key} · ${item.logicalSize}`, {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#f4ead5',
    }).setAlpha(0.45);
  }

  _setupScroll() {
    const cam = this.cameras.main;
    const maxScroll = Math.max(0, this.contentBottom - cam.height + 60);
    cam.setBounds(0, 0, cam.width, this.contentBottom + 60);
    this.maxScroll = maxScroll;

    // Keyboard scroll
    this.input.keyboard.on('keydown', (ev) => {
      if (ev.key === 'ArrowDown' || ev.key === 'j') {
        this._scrollBy(40);
      } else if (ev.key === 'ArrowUp' || ev.key === 'k') {
        this._scrollBy(-40);
      } else if (ev.key === 'PageDown' || ev.key === ' ') {
        this._scrollBy(cam.height - 80);
      } else if (ev.key === 'PageUp') {
        this._scrollBy(-(cam.height - 80));
      } else if (ev.key === 'Home') {
        cam.setScroll(0, 0);
      } else if (ev.key === 'End') {
        cam.setScroll(0, this.maxScroll);
      } else if (ev.key === 'Escape') {
        this._exitToHouse();
      }
    });

    // Wheel scroll
    this.input.on('wheel', (_pointer, _objs, _dx, dy) => {
      this._scrollBy(dy * 0.6);
    });
  }

  _scrollBy(delta) {
    const cam = this.cameras.main;
    const next = Phaser.Math.Clamp(cam.scrollY + delta, 0, this.maxScroll);
    cam.setScroll(0, next);
  }

  _buildFooter() {
    const cam = this.cameras.main;
    const footerH = 28;
    const fy = cam.height - footerH;
    this.add.rectangle(0, fy, cam.width, footerH, 0x14110e, 1)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(900);
    this.add.text(PADDING, fy + 8, '↑↓ / wheel · scroll    PgUp/PgDn · jump    esc · exit lab', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#f4ead5',
    })
      .setAlpha(0.7)
      .setScrollFactor(0)
      .setDepth(901);
  }

  _exitToHouse() {
    if (this._exiting) return;
    this._exiting = true;
    // Drop the hash so a normal reload doesn't bounce back to the lab
    if (window.location.hash === '#lab') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    this.cameras.main.fadeOut(220, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('HouseScene');
    });
  }
}
